import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Button, Checkbox, CircularProgress, debounce, Grid, InputLabel, ListItemText, MenuItem, Select, TextField, Typography, useTheme, } from "@mui/material";
import SubHeader from "../../../../components/SubHeader";
import styles from "./style";
import Modal from "../../../../components/Modal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import TimesheetList from "./TimesheetList";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const TimeManagementPage = ({ data, handleFilter, getTimesheetList, handleTimesheetRequest, handleDeleteEmployee, loading, open, setOpen, handleSort, projectList,
  permission, role, postTimesheet, handleTimesheet, SearchEmployee, employeeData, page, total, setPage, clear, setClear, departmentData, DepartmentPayroll, empData }) => {
  const theme = useTheme();
  const [fromDate, setFromDate] = useState(dayjs());
  const [department, setDepartment] = useState();
  const [item, setItem] = useState();
  const [project, setProject] = useState([{ project: "", minutes: "", projectId: "" }]);
  const [desc, setDesc] = useState("");
  const [from, setFrom] = useState("");
  const [change, setChange] = useState(false);
  const [employeeId, setEmployeeId] = useState([]);
  const employee = localStorage.getItem("employeeId");
  const [selectedOptions, setSelectedOptions] = useState(
    data?.employeeDetails || []
  );
  const [inputValue, setInputValue] = useState("");
  const [activeField, setActiveField] = useState(null);
  const [status, setStatus] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const initialFormStateRef = useRef(null);
  const isEmployeeRole = role === "Employee" || role === "employee";

  useEffect(() => {
    setProject([{ project: "", minutes: "", projectId: "" }]);
    setDesc("");
    setEmployeeId([]);
    setSelectedOptions([]);
    setFromDate(dayjs());
    setStatus(null);
  }, [clear])

  useEffect(() => {
    if (open) {
      if (from === "edit" && item) {
        const initialWorkWithHours = item.work.map(workItem => ({
          ...workItem,
          hours: workItem.minutes
        }));  

        initialFormStateRef.current = {
          work: initialWorkWithHours,
          desc: item.desc,
          fromDate: dayjs(item.date)
        };
        setStatus(item.status || null);
      } else if (from === "create") {
        initialFormStateRef.current = {
          work: [{ project: "", minutes: "", projectId: "", hours: "" }],
          desc: "",
          fromDate: dayjs(),
          employeeId: [],
          department: null
        };
        setDepartment(null);
        setStatus(null);
      }
      setHasChanges(false);
    }
  }, [open, from, item]);

  const checkForChanges = useCallback(() => {
    // if (!initialFormStateRef.current) return false;

    if (from === "edit") {
      const currentWork = item?.work || [];
      const initialWork = initialFormStateRef.current.work || [];

      if (currentWork.length !== initialWork.length) return true;

      for (let i = 0; i < currentWork.length; i++) {
        if (
          currentWork[i]?.project !== initialWork[i]?.project ||
          currentWork[i]?.hours !== initialWork[i]?.hours ||
          currentWork[i]?.projectId !== initialWork[i]?.projectId
        ) {
          return true;
        }
      }

      if (!fromDate.isSame(initialFormStateRef.current.fromDate, 'day')) return true;

      if (desc !== initialFormStateRef.current.desc) return true;

      return false;
    } else if (from === "create") {
      const hasProjectData = project.some(proj =>
        proj.project && proj.minutes && parseInt(proj.minutes) > 0
      );

      return hasProjectData || department || employeeId.length > 0;
    }

    return false;
  }, [from, item, project, desc, fromDate, department, employeeId]);

  useEffect(() => {
    if (open) {
      const changesExist = checkForChanges();
      setHasChanges(changesExist);
    }
  }, [
    open,
    item?.work,
    project,
    desc,
    fromDate,
    department,
    employeeId,
    checkForChanges
  ]);

  const handleRequest = (req) => {
    handleTimesheetRequest(item?._id, req,);
  }

  const handleAddProjectHours = () => {
    if (from === "edit") {
      const updatedItem = JSON.parse(JSON.stringify(item));
      updatedItem.work.push({ project: "", minutes: "", projectId: "", hours: "" });
      setItem(updatedItem);
    } else {
      setProject([...project, { project: "", minutes: "", projectId: "", hours: "" }]);
    }
  };

  const handleCreateTimesheet = () => {
    setProject([{ project: "", minutes: "", projectId: "" }]);
    setDesc("");
    setEmployeeId([]);
    setSelectedOptions([]);
    setFromDate(dayjs());
    setItem(null);
    setFrom("create");
    setOpen(true);
    setDepartment(null);
    setActiveField(null);
    setInputValue("");
    setStatus(null);
  };

  const handleDeleteProjectHours = index => {
    if (from === "edit") {
      const updatedItem = JSON.parse(JSON.stringify(item));
      updatedItem.work.splice(index, 1);
      setItem(updatedItem);
    } else {
      const updatedProjectDetails = [...project];
      updatedProjectDetails.splice(index, 1);
      setProject(updatedProjectDetails);
    }
  };

  const handleEmployee = useCallback(
    debounce((text) => {
      SearchEmployee(text)
    }, 300),
    []
  );

  const SPECIAL_PROJECTS = {
    DEDUCTION: {
      id: "68b00d5d11bedff11e8d4cf1",
      name: "Deduction",
      value: "Deduction"
    },
    HOLIDAY: {
      id: "68b00d5d11bedff11e8d4cf2",
      name: "Holiday",
      value: "Holiday"
    },
    OVERTIME: {
      id: "68b00d5d11bedff11e8d4cf3",
      name: "Overtime",
      value: "overTime"
    },
    EXTRATIME: {
      id: "68b00d5d11bedff11e8d4cf4",
      name: "Extratime",
      value: "extraTime"
    },
    DOUBLE_OVERTIME: {
      id: "68b00d5d11bedff11e8d4cf5",
      name: "Double Overtime",
      value: "doubleOverTime"
    }
  };

  const handleChange = (value, index, key) => {
    if (from === "edit") {
      const updatedItem = JSON.parse(JSON.stringify(item));

      if (key === "hours") {
        updatedItem.work[index].hours = value;
        updatedItem.work[index].minutes = value;
      } else if (key === "project") {
        const specialProject = Object.values(SPECIAL_PROJECTS).find(sp => sp.value === value);
        if (specialProject) {
          updatedItem.work[index].project = specialProject.value;
          updatedItem.work[index].projectId = specialProject.id;
        } else {
          updatedItem.work[index].project = value;
          const selectedProject = projectList.find(proj => proj.name === value);
          updatedItem.work[index].projectId = selectedProject?._id || "";
        }
      }
      setItem(updatedItem);
    } else {
      const updatedProjectDetails = project.map((proj, i) => {
        if (i === index) {
          if (key === "hours") {
            return { ...proj, hours: value, minutes: value };
          } else if (key === "project") {
            const specialProject = Object.values(SPECIAL_PROJECTS).find(sp => sp.value === value);
            if (specialProject) {
              return {
                ...proj,
                project: specialProject.value,
                projectId: specialProject.id
              };
            } else {
              const selectedProject = projectList.find(proj => proj.name === value);
              return {
                ...proj,
                project: value,
                projectId: selectedProject?._id || ""
              };
            }
          }
        }
        return proj;
      });

      setProject(updatedProjectDetails);
    }
  };

  const handleData = () => {
    const selectedEmployeeIds = department ? selectedOptions : employeeId;
    const AllEmployeeID = Array.from(new Set(selectedEmployeeIds));
    const isPrivilegedRole = role === "Admin" || role === "admin" || role === "SuperAdmin";
    const requestId = isPrivilegedRole ? AllEmployeeID : [employee];

    if (from === "edit") {
      const cleanedWork = item?.work?.map(({ hours, ...rest }) => rest);
      handleTimesheet(cleanedWork, fromDate, desc, item?._id);
    } else {
      const cleanedProject = project?.map(({ hours, ...rest }) => rest);
      postTimesheet(cleanedProject, fromDate, desc, requestId);
    }
  };

  const handleDepartmentChange = (e) => {
    const selectedDept = e.target.value;
    setDepartment(selectedDept);

    if (selectedDept) {
      setActiveField('department');
      setEmployeeId([]);
      setInputValue("");
    } else {
      setActiveField(null);
    }

    if (selectedDept === "All") {
      const allEmployeeIds = empData.map((emp) => emp._id);
      setSelectedOptions(allEmployeeIds);
    } else {
      DepartmentPayroll(selectedDept);
      const deptEmployeeIds = empData
        .filter((emp) => emp.personalInfo.department === selectedDept)
        .map((emp) => emp._id);
      setSelectedOptions(deptEmployeeIds);
    }
  };

  const validateCreateMode = () => {
    if (from === "edit") {
      return hasChanges;
    }

    if (!fromDate) return false;
    const hasValidProject = project.some(
      (proj) => proj.project && proj.minutes && parseInt(proj.minutes) > 0
    );
    if (!hasValidProject) return false;

    const hasEmailSelection = employeeId.length > 0;
    const hasDepartmentSelection = !!department;
    if (!hasEmailSelection && !hasDepartmentSelection) return false;
    if (hasEmailSelection && hasDepartmentSelection) return false;

    return true;
  };


  return (
    <>
      <Box px="15px" pt="20px" display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={styles.leaveHeading}>{isEmployeeRole ? "My Time Management" : "Time Management"}</Typography>
        <Button variant="contained" color="success" sx={styles.button} onClick={handleCreateTimesheet}>
          <AddIcon sx={{ fontSize: "14px", }} />
          Create Timesheet
        </Button>
      </Box>
      <Box sx={{ backgroundColor: theme.palette.white.main, m: 2, borderRadius: "20px" }} >
        <SubHeader title={isEmployeeRole ? "My Timesheet" : "Timesheet"}
          search={true}
          sortBy={true}
          onchange={handleFilter}
          handleSort={handleSort}
          onCompanyChange={getTimesheetList}
          selectedOptions={selectedOptions}
        />
        <TimesheetList data={data} setPage={setPage} page={page} total={total} setOpen={setOpen} setItem={setItem} setFrom={setFrom} handleDeleteEmployee={handleDeleteEmployee} permission={permission?.timesheet?.isEdit || role === "Admin" || role === "SuperAdmin"} />
        <Modal open={open} setClear={setClear} setOpen={setOpen} btnText={from === "edit" ? "Save Changes" : "Add New"} handleCheck={() => handleData()} title={from === "create" ? "Create Timesheet" : "Edit Work Details"}
          disabled={!validateCreateMode()} status={status} approveTxt={from === "create" ? false : permission?.timesheet?.isEdit || role === "Admin"} handleRequest={handleRequest}>
          {from !== "edit" &&
            <Box>
              {permission?.timesheet?.isEdit || role === "Admin" ?
                <Grid item xs={12} lg={2}>
                  <Autocomplete
                    id="autocomplete"
                    multiple
                    disableCloseOnSelect
                    disabled={activeField === 'department'}
                    options={employeeData || []}
                    value={employeeData?.filter((emp) => employeeId?.includes(emp._id))}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason !== "reset") {
                        setInputValue(newInputValue);
                        handleEmployee(newInputValue);
                      }
                    }}
                    onChange={(event, newValue, reason) => {
                      const selectedIds = newValue?.map((emp) => emp?._id);
                      setEmployeeId(selectedIds);
                      if (selectedIds.length > 0) {
                        setActiveField('email');
                        setDepartment(null);
                      } else {
                        setActiveField(null);
                      }

                      if (reason === "selectOption") {
                        setInputValue("");
                      }
                    }}
                    getOptionLabel={(option) => option?.personalInfo?.email || ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    renderTags={() => null}
                    renderOption={(props, option, { selected }) => (
                      <MenuItem {...props}>
                        <Checkbox checked={selected} style={{ marginRight: 8 }} />
                        <ListItemText primary={option?.personalInfo?.email} />
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <>
                        <InputLabel sx={styles.label}>Employee Email</InputLabel>
                        <TextField
                          {...params}
                          sx={{ ...styles.modalInput, ...styles.inputSpace }}
                          variant="outlined"
                          placeholder="Enter Employee Email"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      </>
                    )}
                  />
                </Grid>
                : null}
            </Box>}
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <InputLabel sx={styles.labels}>Date</InputLabel>
              <DatePicker value={fromDate} onChange={(newValue) => { setFromDate(newValue); }} sx={styles.dateInput} />
            </LocalizationProvider>
            {from !== "edit" &&
              <Box sx={{ width: "100%", marginTop: "26px", mr: 1 }}>
                <InputLabel sx={styles.label}>Department</InputLabel>
                <Select
                  value={department}
                  onChange={handleDepartmentChange}
                  disabled={activeField === 'email'}
                  sx={{ ...styles.modalInput, borderRadius: "8px", my: "10px" }}
                  renderValue={(selected) => (selected ? selected : "Select Department")}
                  displayEmpty
                >
                  <MenuItem disabled value="">Select</MenuItem>
                  <MenuItem value="All">All</MenuItem>
                  {departmentData?.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item?.name}
                      onClick={() => DepartmentPayroll(item?.name)}
                    >
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            }
          </Box>

          {from !== "edit" ? project.map((ph, index) => {
            return (
              <Box key={index} sx={styles.projectContainers}>
                <Box sx={{ display: "flex" }}>
                  <Box>
                    <Box sx={styles.projectContainer}>
                      <Box sx={{ width: "232px" }}>
                        <InputLabel sx={styles.labels}>Project</InputLabel>
                        <Select
                          fullWidth
                          sx={{ ...styles.dropdown, width: "100%" }}
                          value={ph?.project || ""}
                          onChange={(e) => {
                            handleChange(e.target.value, index, "project");
                            setChange(true);
                          }}
                          onBlur={() => setChange(false)}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span style={{ color: "#aaa" }}>Select</span>;
                            }
                            return selected;
                          }}
                        >
                          <MenuItem value={""} style={styles.dropdownMenu} disabled>Select</MenuItem>
                          {projectList?.map((item, index) => (
                            <MenuItem value={item?.name} key={index} style={styles.dropdownMenu}>
                              {item?.name}
                            </MenuItem>
                          ))}
                          <MenuItem value={SPECIAL_PROJECTS.DEDUCTION.value} style={styles.dropdownMenu}>
                            {SPECIAL_PROJECTS.DEDUCTION.name}
                          </MenuItem>
                          <MenuItem value={SPECIAL_PROJECTS.HOLIDAY.value} style={styles.dropdownMenu}>
                            {SPECIAL_PROJECTS.HOLIDAY.name}
                          </MenuItem>
                          <MenuItem value={SPECIAL_PROJECTS.OVERTIME.value} style={styles.dropdownMenu}>
                            {SPECIAL_PROJECTS.OVERTIME.name}
                          </MenuItem>
                          <MenuItem value={SPECIAL_PROJECTS.EXTRATIME.value} style={styles.dropdownMenu}>
                            {SPECIAL_PROJECTS.EXTRATIME.name}
                          </MenuItem>
                          <MenuItem value={SPECIAL_PROJECTS.DOUBLE_OVERTIME.value} style={styles.dropdownMenu}>
                            {SPECIAL_PROJECTS.DOUBLE_OVERTIME.name}
                          </MenuItem>
                        </Select>
                      </Box>
                      <Box sx={{ width: "232px" }}>
                        <InputLabel sx={styles.labels}>Hours</InputLabel>
                        <TextField
                          placeholder="Hours"
                          type="number"
                          sx={{ ...styles.hoursInput, width: "100%" }}
                          value={ph?.hours}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              handleChange(value, index, "hours");
                              setChange(true);
                            }
                          }}
                          onBlur={() => setChange(false)}
                          inputProps={{
                            min: "0.1",
                            step: "0.1",
                            pattern: "[0-9]*\\.?[0-9]*"
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    {index === project.length - 1 ? (
                      <Box sx={styles.iconstyle} onClick={handleAddProjectHours}>
                        <AddIcon sx={{ color: "#28AA4A" }} />
                      </Box>
                    ) : (
                      <Box sx={styles.deleteIconstyle} onClick={() => handleDeleteProjectHours(index)}>
                        <DeleteOutlineIcon sx={{ color: "#E93030" }} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          }) : item?.work?.map((ph, index) => {
            return (<Box key={index} sx={styles.projectContainers}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box>
                  <Grid container sx={styles.gridSection}>
                    <Grid item xs={6} md={6.5} sx={styles.checkboxGrid}>
                      <InputLabel sx={styles.labels}>Project</InputLabel>
                      <Select sx={{
                        ...styles.dropdown,
                        width: "232px",
                        height: "40px",
                      }} value={ph?.project || ""} onChange={(e) => {
                        handleChange(e.target.value, index, "project");
                        setChange(true);
                      }} onBlur={() => setChange(false)} renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: "#aaa" }}>Select</span>;
                        }
                        return selected;
                      }}>
                        {projectList?.map((item, index) => (<MenuItem value={item?.name} key={index} style={styles.dropdownMenu}>
                          {item?.name}
                        </MenuItem>))}
                        <MenuItem value={SPECIAL_PROJECTS.DEDUCTION.value} style={styles.dropdownMenu}>
                          {SPECIAL_PROJECTS.DEDUCTION.name}
                        </MenuItem>
                        <MenuItem value={SPECIAL_PROJECTS.HOLIDAY.value} style={styles.dropdownMenu}>
                          {SPECIAL_PROJECTS.HOLIDAY.name}
                        </MenuItem>
                        <MenuItem value={SPECIAL_PROJECTS.OVERTIME.value} style={styles.dropdownMenu}>
                          {SPECIAL_PROJECTS.OVERTIME.name}
                        </MenuItem>
                        <MenuItem value={SPECIAL_PROJECTS.EXTRATIME.value} style={styles.dropdownMenu}>
                          {SPECIAL_PROJECTS.EXTRATIME.name}
                        </MenuItem>
                        <MenuItem value={SPECIAL_PROJECTS.DOUBLE_OVERTIME.value} style={styles.dropdownMenu}>
                          {SPECIAL_PROJECTS.DOUBLE_OVERTIME.name}
                        </MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={6} md={5} sx={styles.checkboxGrid}>
                      <InputLabel sx={styles.labels}>Hours</InputLabel>
                      <TextField sx={{ ...styles.hoursInput, width: "232px", height: "40px" }} value={ph?.hours}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            handleChange(value, index, "hours");
                            setChange(true);
                          }
                        }} onBlur={() => setChange(false)} inputProps={{
                          min: "0.1",
                          step: "0.1",
                          pattern: "[0-9]*\\.?[0-9]*"
                        }} />
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ ml: 5, mt: 1.5 }}>
                  {index === item.work.length - 1 ? (<Box sx={styles.iconstyle} onClick={permission?.timesheet?.isEdit || role === "Admin" ? handleAddProjectHours : null}>
                    <AddIcon sx={{ color: "#28AA4A" }} />
                  </Box>) : (<Box sx={styles.deleteIconstyle} onClick={() => permission?.timesheet?.isEdit || role === "Admin" ? handleDeleteProjectHours(index) : null}>
                    <DeleteOutlineIcon sx={{ color: "#E93030" }} />
                  </Box>)}
                </Box>
              </Box>
            </Box>);
          })}

          <Box sx={{ my: 1, marginTop: '26px' }}>
            <InputLabel sx={styles.labels}>Description</InputLabel>
            <TextField
              multiline={true}
              value={change ? desc : item?.desc}
              onChange={(e) => { setDesc(e.target.value); setChange(true); }}
              rows={2}
              sx={styles.reason}
            />
          </Box>
        </Modal>

      </Box>
    </>
  );
};

export default TimeManagementPage;
